import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable for checking user permissions and roles (RBAC-SLICE-3 seam).
 *
 * Prefer `can` / `canAny` for new code. Reads /auth/me permissions via authStore
 * (effective grants during impersonation — no special casing).
 *
 * Super.admin (and legacy super-admin role) bypass is implemented once in authStore.
 */
export function usePermissions() {
  const authStore = useAuthStore()

  const user = computed(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)

  /** Exact permission check; super.admin → true for any string. */
  const can = (permission: string): boolean => {
    return authStore.hasPermission(permission)
  }

  /** True if user holds any of the listed permissions (or is super.admin). */
  const canAny = (permissions: string[]): boolean => {
    return authStore.hasAnyPermission(permissions)
  }

  /** @deprecated Prefer `can` — same implementation. */
  const hasPermission = (permission: string): boolean => can(permission)

  /** @deprecated Prefer `canAny` — same implementation. */
  const hasAnyPermission = (permissions: string[]): boolean => canAny(permissions)

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return authStore.hasAllPermissions(permissions)
  }

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return authStore.hasRole(role)
  }

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return authStore.hasAnyRole(roles)
  }

  /**
   * Check if user is a super admin (permissions-only, no role checks)
   */
  const isSuperAdmin = computed(() => hasPermission('super.admin'))

  /**
   * Check if user is an admin (permissions-only, no role checks)
   * Note: This is for backward compatibility in UI. Authorization should use specific permissions.
   */
  const isAdmin = computed(() => {
    // Permissions-only authorization - super.admin bypass is handled by hasPermission()
    return hasPermission('super.admin')
  })

  /**
   * Check if user can manage PIM (edit/delete products)
   * Permissions-only authorization (no role checks)
   */
  const canManagePim = computed(() => {
    // Super admin bypass is handled by hasPermission()
    return hasPermission('pim.products.create') || 
           hasPermission('pim.products.edit') || 
           hasPermission('pim.products.delete') ||
           hasPermission('pim.categories.create') ||
           hasPermission('pim.categories.edit')
  })

  /**
   * Check if user can manage CRM (edit/delete accounts, clients, projects, tasks, notes)
   * Permissions-only authorization (no role checks)
   */
  const canManageCrm = computed(() => {
    // Super admin bypass is handled by hasPermission()
    return hasPermission('crm.accounts.create') || 
           hasPermission('crm.accounts.edit') ||
           hasPermission('crm.contacts.create') ||
           hasPermission('crm.contacts.edit') ||
           hasPermission('crm.projects.create') ||
           hasPermission('crm.projects.edit') ||
           hasPermission('crm.tasks.create') ||
           hasPermission('crm.tasks.edit')
  })

  /**
   * Check if user can manage Site Surveys
   * Permissions-only authorization (no role checks)
   */
  const canManageSs = computed(() => {
    // Super admin bypass is handled by hasPermission()
    return hasPermission('surveys.surveys.create') || 
           hasPermission('surveys.surveys.edit') ||
           hasPermission('surveys.surveys.delete') ||
           hasPermission('surveys.inquiries.create') ||
           hasPermission('surveys.inquiries.edit')
  })

  /**
   * Check if user can manage RD (Research & Development)
   * Permissions-only authorization (no role checks)
   */
  const canManageRd = computed(() => {
    // Super admin bypass is handled by hasPermission()
    return hasPermission('rd.timephases.create') || 
           hasPermission('rd.timephases.edit') ||
           hasPermission('rd.estimates.create') ||
           hasPermission('rd.estimates.edit') ||
           hasPermission('rd.dashboards.manage') ||
           hasPermission('rd.flashreports.create') ||
           hasPermission('rd.flashreports.edit')
  })

  /**
   * Check if user can manage Machines
   * Permissions-only authorization (no role checks)
   */
  const canManageMachines = computed(() => {
    // Super admin bypass is handled by hasPermission()
    return hasPermission('machines.machines.create') || 
           hasPermission('machines.machines.edit') ||
           hasPermission('machines.machines.delete')
  })

  /**
   * Check if user can submit GREEN status (repair confirmation)
   * Requires operations admin permission
   */
  const canSubmitGreenStatus = computed(() => {
    // Super admin bypass is handled by hasPermission()
    return hasPermission('machines.machines.edit') || hasPermission('operations.downtime.create')
  })

  return {
    user,
    isAuthenticated,
    can,
    canAny,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isAdmin,
    canManagePim,
    canManageCrm,
    canManageSs,
    canManageRd,
    canManageMachines,
    canSubmitGreenStatus,
  }
}

